import { useState } from 'react';
import { Box } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { getHistoryReservationsApi } from '../../utils/apis';
import ReservationTableSkeleton from './ReservationTableSkeleton';
import { useUser } from '../../hooks/useUser';
import NoDataFallback from '../common/NoDataFallback';

interface OrderHistoryProps {
  fromDate: Date;
  toDate: Date;
  selectedStore: any;
  isStoresLoading: boolean;
}

const HistoryReservation = ({
  fromDate,
  toDate,
  selectedStore,
  isStoresLoading,
}: OrderHistoryProps) => {
  const { user } = useUser();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  const formatDateForApi = (date: Date) => {
    return date.toLocaleDateString('en-GB').replace(/\//g, '-');
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [
      'hisstory-reservations',
      paginationModel.page,
      paginationModel.pageSize,
      fromDate.toISOString(),
      toDate.toISOString(),
      selectedStore?.reservation?.restaurantId,
    ],
    queryFn: () =>
      getHistoryReservationsApi({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        endDate: formatDateForApi(toDate),
        startDate: formatDateForApi(fromDate),
        restaurantId: selectedStore?.reservation?.restaurantId,
        userId: user?._id,
      }),
    enabled: Boolean(selectedStore?.reservation?.restaurantId),
    gcTime: 1000 * 60 * 10,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  if (isLoading || isStoresLoading) {
    return <ReservationTableSkeleton />;
  }

  if (isError && error) {
    return (
      <NoDataFallback
        message="No reservations available for the selected date range"
        description="Looks like there are no reservations to display right now. Once you start receiving reservations, they’ll show up here"
      />
    );
  }

  if (!data || data?.data?.data?.reservations?.length === 0) {
    return (
      <NoDataFallback
        message="No reservations available for the selected date range"
        description="Looks like there are no reservations to display right now. Once you start receiving reservations, they’ll show up here"
      />
    );
  }

  const transformedOrders =
    data?.data?.data?.reservations
      ?.map((booking: any) => {
        // Parse the date strings into Date objects
        const parseDate = (dateStr: string) => {
          if (!dateStr) return null;
          try {
            // Split the date string into components
            const [datePart, timePart] = dateStr.split(', ');
            const [day, month, year] = datePart.split('/');
            const [time, period] = timePart.split(' ');
            const [hours, minutes] = time.split(':');

            // Convert to 24-hour format if PM
            let hour = parseInt(hours);
            if (period.toLowerCase() === 'pm' && hour !== 12) {
              hour += 12;
            } else if (period.toLowerCase() === 'am' && hour === 12) {
              hour = 0;
            }

            // Create new Date object (months are 0-based in JavaScript)
            return new Date(
              parseInt(year),
              parseInt(month) - 1,
              parseInt(day),
              hour,
              parseInt(minutes)
            );
          } catch (error) {
            console.error('Error parsing date:', dateStr, error);
            return null;
          }
        };

        // Format the date for display
        const formatDate = (date: Date | null) => {
          if (!date || isNaN(date.getTime())) return '-';

          const dateStr = date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          });

          const timeStr = date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          });

          return `${dateStr} ${timeStr}`;
        };

        // Parse the dates
        const reservationDate = parseDate(booking.tpReservationTime);
        const createdDate = parseDate(booking.createdAt);

        return {
          rgBookingId: booking.ReservationId || '-',
          tpReservationTime: formatDate(reservationDate),
          _rawReservationTime: reservationDate, // Store raw date for sorting
          tpGuestName: booking.tpGuestName || '-',
          tpGuestCount: booking.tpGuestCount || '-',
          tpGuestPhoneNumber: booking.tpGuestPhoneNumber || '-',
          tpGuestEmail: booking.tpGuestEmail || '-',
          tpReservationStatus: booking.tpReservationStatus || '-',
          tpMessage: booking.tpGuestComments || '-',
          createdAt: formatDate(createdDate),
          _rawCreatedAt: createdDate, // Store raw date for sorting
        };
      })
      ?.sort((a: any, b: any) => {
        // Sort using the raw Date objects
        const dateA = a._rawReservationTime;
        const dateB = b._rawReservationTime;

        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        return dateB.getTime() - dateA.getTime(); // Sort in descending order (newest first)
      }) || [];

  // Remove the raw date fields before passing to DataGrid
  const cleanedOrders = transformedOrders.map((order: any) => {
    const { ...rest } = order;
    return rest;
  });

  // Transform API data to match the expected format for the DataGrid

  const getReservationStatusColor = (status: string) => {
    const normalizedStatus = status.toLowerCase();

    switch (normalizedStatus) {
      case 'pending':
        return 'rgba(244, 197, 66, 0.2)';
      case 'confirmed':
        return 'rgba(55, 183, 125, 0.2)';
      case 'cancelled':
        return 'rgba(231, 76, 60, 0.2)';
      case 'waitlisted':
        return 'rgba(90, 76, 230, 0.1)';
      default:
        return 'rgba(169, 169, 169, 0.2)';
    }
  };

  const getReservationStatusTextColor = (status: string) => {
    const normalizedStatus = status.toLowerCase();

    switch (normalizedStatus) {
      case 'pending':
        return 'rgba(43, 45, 63, 1)';
      case 'confirmed':
        return 'rgba(43, 45, 63, 1)';
      case 'cancelled':
        return 'rgba(43, 45, 63, 1)';
      case 'waitlisted':
        return 'rgba(43, 45, 63, 1)';
      default:
        return 'rgba(43, 45, 63, 1)';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'rgBookingId',
      headerName: 'Booking Id',
      width: 230,
      headerClassName: 'custom-header',
    },
    {
      field: 'tpReservationTime',
      headerName: 'Scheduled For',
      width: 200,
      headerClassName: 'custom-header',
    },
    {
      field: 'tpGuestName',
      headerName: 'Name',
      width: 150,
      headerClassName: 'custom-header',
    },
    {
      field: 'tpGuestCount',
      headerName: 'Party Size',
      width: 120,
      headerClassName: 'custom-header',
    },
    {
      field: 'tpGuestPhoneNumber',
      headerName: 'Phone',
      width: 150,
      headerClassName: 'custom-header',
      renderCell: (params: GridRenderCellParams) => `+${params.value}`,
    },
    {
      field: 'tpGuestEmail',
      headerName: 'Email',
      width: 150,
      headerClassName: 'custom-header',
    },
    {
      field: 'tpMessage',
      headerName: 'Message',
      width: 180,
      headerClassName: 'custom-header',
    },
    {
      field: 'tpReservationStatus',
      headerName: 'Status',
      width: 120,
      headerClassName: 'custom-header',
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            backgroundColor: getReservationStatusColor(params.value.toString()),
            color: getReservationStatusTextColor(params.value.toString()),
            width: 'auto',
            minWidth: 78,
            maxWidth: 130,
            height: 24,
            borderRadius: '6px',
            padding: '4px 8px',
            fontSize: '12px',
            fontWeight: '400',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          {params.value}
        </Box>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created On',
      width: 180,
      headerClassName: 'custom-header',
    },
  ];

  const handlePaginationModelChange = (newModel: any) => {
    // If page size changed, go back to first page
    if (newModel.pageSize !== paginationModel.pageSize) {
      setPaginationModel({
        page: 0,
        pageSize: newModel.pageSize,
      });
    } else {
      setPaginationModel(newModel);
    }
  };

  return (
    <Box sx={{ height: '100%', width: '100%' }}>
      <DataGrid
        rows={cleanedOrders}
        columns={columns}
        getRowId={row => row.rgBookingId}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[10, 20, 50, 75, 100]}
        checkboxSelection={false}
        rowCount={data?.data?.data?.pagination?.totalDocs || 0}
        paginationMode="server"
        disableRowSelectionOnClick
        sx={{
          borderRadius: '8px',
          color: 'rgba(107, 111, 128, 1)',
          '& .MuiDataGrid-root': {
            borderRadius: '8px',
            overflow: 'hidden',
          },
          '& .MuiDataGrid-main': {
            borderRadius: '8px',
            overflow: 'hidden',
          },
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            padding: '12px 16px',
          },
          '& .MuiDataGrid-columnHeader': {
            display: 'flex',
            alignItems: 'center',
            padding: '12px 16px',
          },
          '& .custom-header': {
            bgcolor: '#F8F9FC',
            color: 'custom.textBlue',
            fontSize: '14px',
            fontWeight: 600,
            padding: '12px 16px',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            color: 'rgba(43, 45, 63, 1)',
            fontWeight: 600,
          },
          '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: 'transparent !important',
            border: 'none !important',
          },
          '& .MuiDataGrid-row.Mui-selected:hover': {
            backgroundColor: '#E7F0FF !important',
          },
          '& .MuiDataGrid-row:focus, & .MuiDataGrid-cell:focus': {
            outline: 'none !important',
          },
          '& .MuiDataGrid-cell:focus-within': {
            outline: 'none !important',
          },
        }}
      />
    </Box>
  );
};

export default HistoryReservation;
